import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

interface EmbedConfig {
  isEmbed: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  compactMode?: boolean;
  parentOrigin?: string;
  directService?: 'tenancy_review_rewrite' | 'commercial_lease_review';
}

export interface UserContext {
  userId: string;
  email: string;
  organizationId?: string;
  subscriptionTier: 'free' | 'basic' | 'professional' | 'enterprise';
  properties: Property[];
}

export interface Property {
  id: string;
  address: string;
  propertyType: string;
  metadata?: Record<string, any>;
}

export interface DocumentSavePayload {
  documentGenerationId: string;
  documentType: string;
  propertyId?: string;
  standardContent: string;
  enhancedContent?: string;
  premiumContent?: string;
  formData: Record<string, any>;
  subscriptionTier: string;
  unlockedTiers: string[];
  timestamp: number;
}

interface EmbedContextType {
  config: EmbedConfig;
  userContext: UserContext | null;
  sendMessage: (type: string, data?: any) => void;
  requestUserContext: () => Promise<UserContext | null>;
  saveDocumentToParent: (payload: DocumentSavePayload) => Promise<{ success: boolean; parentDocumentId?: string }>;
  notifyDocumentGenerated: (documentId: string, documentType: string) => void;
}

const EmbedContext = createContext<EmbedContextType | undefined>(undefined);

export function EmbedProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<EmbedConfig>(() => {
    const params = new URLSearchParams(window.location.search);
    let isInIframe = false;
    try {
      isInIframe = window.self !== window.top;
    } catch {
      isInIframe = true;
    }
    const embedParam = params.get('embed') === 'true';
    const serviceParam = params.get('service');
    const validServices = ['tenancy_review_rewrite', 'commercial_lease_review'] as const;
    const directService = validServices.includes(serviceParam as any)
      ? (serviceParam as typeof validServices[number])
      : undefined;

    return {
      isEmbed: isInIframe || embedParam,
      hideHeader: params.get('hideHeader') === 'true',
      hideFooter: params.get('hideFooter') === 'true',
      compactMode: params.get('compactMode') === 'true',
      parentOrigin: params.get('parentOrigin') || '*',
      directService,
    };
  });

  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [messageCallbacks, setMessageCallbacks] = useState<Map<string, (data: any) => void>>(new Map());

  const sendMessage = (type: string, data?: any) => {
    if (config.isEmbed && window.parent) {
      window.parent.postMessage(
        {
          type,
          source: 'tenancy-agreement-embed',
          timestamp: Date.now(),
          ...data,
        },
        config.parentOrigin || '*'
      );
    }
  };

  const requestUserContext = useCallback((): Promise<UserContext | null> => {
    return new Promise((resolve) => {
      if (!config.isEmbed) {
        resolve(null);
        return;
      }

      const requestId = `userContext_${Date.now()}`;
      const timeoutId = setTimeout(() => {
        messageCallbacks.delete(requestId);
        resolve(null);
      }, 5000);

      messageCallbacks.set(requestId, (data) => {
        clearTimeout(timeoutId);
        messageCallbacks.delete(requestId);
        setUserContext(data);
        resolve(data);
      });

      sendMessage('getUserContext', { requestId });
    });
  }, [config.isEmbed, sendMessage]);

  const saveDocumentToParent = useCallback((payload: DocumentSavePayload): Promise<{ success: boolean; parentDocumentId?: string }> => {
    return new Promise((resolve) => {
      if (!config.isEmbed) {
        resolve({ success: false });
        return;
      }

      const requestId = `saveDocument_${Date.now()}`;
      const timeoutId = setTimeout(() => {
        messageCallbacks.delete(requestId);
        resolve({ success: false });
      }, 10000);

      messageCallbacks.set(requestId, (data) => {
        clearTimeout(timeoutId);
        messageCallbacks.delete(requestId);
        resolve(data);
      });

      sendMessage('saveToMyDocuments', {
        requestId,
        payload,
      });
    });
  }, [config.isEmbed, sendMessage]);

  const notifyDocumentGenerated = useCallback((documentId: string, documentType: string) => {
    sendMessage('documentGenerated', { documentId, documentType });
  }, [sendMessage]);

  useEffect(() => {
    const handleResize = () => {
      if (config.isEmbed) {
        sendMessage('resize', {
          height: document.documentElement.scrollHeight,
          width: document.documentElement.scrollWidth,
        });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [config.isEmbed]);

  useEffect(() => {
    if (config.isEmbed) {
      sendMessage('ready');
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.source === 'tenancy-agreement-parent') {
        const { type, requestId, payload } = event.data;

        if (requestId && messageCallbacks.has(requestId)) {
          const callback = messageCallbacks.get(requestId);
          callback?.(payload);
        }

        switch (type) {
          case 'userContextResponse':
            if (payload) {
              setUserContext(payload);
            }
            break;
          case 'documentSaveResponse':
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [messageCallbacks]);

  return (
    <EmbedContext.Provider
      value={{
        config,
        userContext,
        sendMessage,
        requestUserContext,
        saveDocumentToParent,
        notifyDocumentGenerated,
      }}
    >
      {children}
    </EmbedContext.Provider>
  );
}

export function useEmbed() {
  const context = useContext(EmbedContext);
  if (context === undefined) {
    return {
      config: { isEmbed: false },
      userContext: null,
      sendMessage: () => {},
      requestUserContext: async () => null,
      saveDocumentToParent: async () => ({ success: false }),
      notifyDocumentGenerated: () => {},
    };
  }
  return context;
}

export function useEmbedMode() {
  const { config } = useEmbed();
  return config.isEmbed;
}
